/**
 * 契約組織の詳細 `/organizations/$orgId`。
 * タブ: 概要 / ドメイン / メールボックス / 使用量 / 請求 / 監査
 */
import * as React from 'react';
import { Link, getRouteApi } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Ban,
  Building2,
  CreditCard,
  FileSearch,
  Globe,
  Mail,
  MoveRight,
  Pencil,
  Printer,
  Trash2,
  TrendingUp,
  Undo2,
  UserPlus,
} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  CursorPagination,
  DataTable,
  Input,
  KeyValue,
  PageHeader,
  Section,
  StatCard,
  StatusBadge,
  Textarea,
  UsageBar,
  formatBytes,
  formatCount,
  formatDateTime,
  formatRelative,
  type DataTableColumn,
} from '@mail-sv/ui';
import { labelOf } from '@mail-sv/shared';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { formatJpy } from '../lib/format.js';
import { useCursorList } from '../lib/use-cursor-list.js';
import { OPS_PERMISSION } from '../lib/permissions.js';
import { usePermission } from '../stores/auth.js';
import { PageTabs } from '../components/page-tabs.js';
import { ConfirmTextDialog, FormDialog } from '../components/dialogs.js';
import { Field, NativeSelect } from '../components/form-fields.js';
import { LineChart, type Series } from '../components/spark-chart.js';
import { DetailSkeleton, QueryError, RefetchingDot } from '../components/states.js';
import { RID } from './ids.js';
import type {
  AbuseReport,
  AuditEntry,
  DomainSummary,
  IncidentSummary,
  InvoiceSummary,
  MailboxSummary,
  NodeSummary,
  OrganizationDetail,
} from '../types/api.js';

const routeApi = getRouteApi(RID.organizationDetail);

export function OrganizationDetailPage(): React.ReactElement {
  const { orgId } = routeApi.useParams();
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();

  const query = useQuery({
    queryKey: qk.org(orgId),
    queryFn: () => api.get<OrganizationDetail>(`/orgs/${orgId}`),
  });

  const org = query.data;

  return (
    <>
      <PageHeader
        title={org?.displayName ?? '契約組織'}
        description={org ? `${org.slug} / ${org.billingEmail}` : undefined}
        breadcrumbs={[{ label: '契約組織', href: '/organizations' }, { label: org?.displayName ?? '…' }]}
        actions={
          <>
            <RefetchingDot active={query.isFetching && !query.isLoading} />
            {org ? <StatusBadge status={org.status} dot /> : null}
            {org ? <StatusBadge status={org.subscriptionStatus} /> : null}
          </>
        }
      />

      {query.isError ? <QueryError error={query.error} onRetry={() => void query.refetch()} /> : null}
      {query.isLoading ? <DetailSkeleton /> : null}

      {org ? (
        <>
          {org.status === 'suspended' ? (
            <Alert variant="destructive" className="mb-5">
              <AlertTitle>この組織は停止中です</AlertTitle>
              <AlertDescription>{org.suspendedReason ?? '理由が記録されていません。'}</AlertDescription>
            </Alert>
          ) : null}

          <PageTabs
            value={search.tab}
            onChange={(tab) => void navigate({ search: (prev) => ({ ...prev, tab }) })}
            tabs={[
              { value: 'overview', label: '概要', icon: Building2 },
              { value: 'domains', label: 'ドメイン', icon: Globe },
              { value: 'mailboxes', label: 'メールボックス', icon: Mail },
              { value: 'usage', label: '使用量', icon: TrendingUp },
              { value: 'invoices', label: '請求', icon: CreditCard },
              { value: 'activity', label: '監査', icon: FileSearch },
            ]}
          />

          {search.tab === 'overview' ? <OverviewTab org={org} /> : null}
          {search.tab === 'domains' ? <DomainsTab orgId={orgId} /> : null}
          {search.tab === 'mailboxes' ? <MailboxesTab orgId={orgId} /> : null}
          {search.tab === 'usage' ? <UsageTab orgId={orgId} /> : null}
          {search.tab === 'invoices' ? <InvoicesTab orgId={orgId} /> : null}
          {search.tab === 'activity' ? <ActivityTab orgId={orgId} /> : null}
        </>
      ) : null}
    </>
  );
}

/* ---------------------------------------------------------------- overview */

function OverviewTab({ org }: { org: OrganizationDetail }): React.ReactElement {
  const canWrite = usePermission(OPS_PERMISSION.orgWrite);
  const [editOpen, setEditOpen] = React.useState(false);
  const [inviteOpen, setInviteOpen] = React.useState(false);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="ドメイン" value={formatCount(org.domainCount)} icon={Globe} />
        <StatCard label="メールボックス" value={formatCount(org.mailboxCount)} icon={Mail} />
        <StatCard label="使用容量" value={formatBytes(org.storageUsedBytes)} icon={TrendingUp} />
        <StatCard label="月額" value={formatJpy(org.monthlyAmountJpy)} icon={CreditCard} />
      </div>

      <Section
        title="契約情報"
        actions={
          canWrite ? (
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
              <Pencil aria-hidden />
              マスタを修正
            </Button>
          ) : undefined
        }
      >
        <div className="rounded-lg border border-border bg-card p-4">
          <KeyValue
            columns={3}
            items={[
              { key: '組織名', value: org.displayName },
              { key: 'スラッグ', value: <code className="font-mono text-xs">{org.slug}</code> },
              { key: '連絡先', value: org.billingEmail },
              { key: 'プラン', value: org.planName },
              { key: '契約状態', value: <StatusBadge status={org.subscriptionStatus} size="sm" /> },
              { key: '次回請求日', value: formatDateTime(org.nextInvoiceAt) },
              {
                key: '主収容ノード',
                value: org.primaryNodeId ? (
                  <Link
                    to="/nodes/$nodeId"
                    params={{ nodeId: org.primaryNodeId }}
                    search={{ tab: 'overview', range: '24h' }}
                    className="hover:text-primary"
                  >
                    {org.primaryNodeHostname ?? org.primaryNodeId}
                  </Link>
                ) : (
                  '—'
                ),
              },
              { key: 'Webメール同期', value: <StatusBadge status={org.webmailSyncState} size="sm" /> },
              { key: '登録日', value: formatDateTime(org.createdAt) },
            ]}
          />
          {org.notes ? (
            <p className="mt-4 whitespace-pre-wrap rounded-md bg-panel p-3 text-sm text-muted-foreground">
              {org.notes}
            </p>
          ) : null}
        </div>
        <EditOrganizationDialog org={org} open={editOpen} onOpenChange={setEditOpen} />
      </Section>

      <Section title="容量">
        <div className="rounded-lg border border-border bg-card p-4">
          <UsageBar
            used={org.storageUsedBytes}
            total={org.storageLimitBytes}
            label="ストレージ"
            formatValue={(v) => formatBytes(v)}
          />
          {org.overrides ? (
            <p className="mt-3 text-xs text-muted-foreground">
              個別上限あり — メールボックス {org.overrides.mailboxLimit ?? 'プラン既定'} / ストレージ{' '}
              {org.overrides.storageBytes ? formatBytes(org.overrides.storageBytes) : 'プラン既定'} / 日次送信{' '}
              {org.overrides.dailySendLimit ?? 'プラン既定'}
            </p>
          ) : null}
        </div>
      </Section>

      <Section
        title="メンバー"
        actions={
          canWrite ? (
            <Button size="sm" variant="outline" onClick={() => setInviteOpen(true)}>
              <UserPlus aria-hidden />
              担当者を招待
            </Button>
          ) : null
        }
      >
        {org.members.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            メンバーが登録されていません。<strong>このままでは誰も契約者パネルにログインできません。</strong>
          </p>
        ) : (
          <div className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
            {org.members.map((m) => (
              <div key={m.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5 text-sm">
                <span className="min-w-0 flex-1 truncate font-medium">{m.displayName}</span>
                <span className="min-w-0 truncate text-muted-foreground">{m.email}</span>
                <Badge variant="muted" size="sm">
                  {labelOf(m.role)}
                </Badge>
                <span className="text-xs text-muted-foreground">最終ログイン {formatRelative(m.lastLoginAt)}</span>
              </div>
            ))}
          </div>
        )}
      </Section>

      <InviteOwnerDialog orgId={org.id} open={inviteOpen} onOpenChange={setInviteOpen} />

      <RecentIncidents orgId={org.id} />

      {canWrite ? <DangerZone org={org} /> : null}
    </div>
  );
}

/* --------------------------------------------------- 直近インシデント */

/**
 * この契約組織が巻き込まれた障害（`incidents.affected_organization_ids`）。
 *
 * 問い合わせの一次対応で最初に要るのが「この契約者は直近の障害の影響を
 * 受けているか」なのに、判断材料が `/incidents` の全社一覧しか無く、
 * **1 件ずつ影響範囲を開いて確かめる**しかなかった。
 *
 * **`GET /ops/incidents` は組織で絞れない**（cursor と limit だけ）。
 * 直近 50 件を引いて画面側で突き合わせる。インシデントは 1 日に何件も
 * 起きるものではないので 50 件あれば数か月分をカバーでき、
 * 「全件を舐める」ことにはならない。API に `organizationId` が付いたら
 * ここはサーバー側の絞り込みに置き換えること。
 */
function RecentIncidents({ orgId }: { orgId: string }): React.ReactElement | null {
  const canRead = usePermission(OPS_PERMISSION.securityRead);
  const query = useQuery({
    queryKey: qk.incidents({ recentForOrg: orgId }),
    queryFn: () => api.get<{ items: IncidentSummary[] }>('/incidents', { limit: 50 }),
    enabled: canRead,
  });

  /*
   * **迷惑行為の通報もここに出す（R77）。**
   *
   * 「この顧客に何が起きたか」を運営が辿るとき、障害（incidents）と
   * 通報（abuse_reports）は必ずセットで見る。片方だけ出すと、
   * 「障害は無いのに送信が止まっている」の理由がこの画面から辿れない。
   *
   * **絞り込みはクライアント側**。`GET /ops/abuse/reports` は `state` しか
   * 受けず、`organizationId` の絞りが無い（`GET /ops/incidents` も同じ）。
   * 直近 50 件を引いて該当を拾う形にしてあるので、通報が多い時期には
   * 取りこぼしうる。**サーバー側に organizationId フィルタを足すのが本筋。**
   */
  const abuse = useQuery({
    queryKey: qk.abuseReports({ recentForOrg: orgId }),
    queryFn: () => api.get<{ items: AbuseReport[] }>('/abuse/reports', { limit: 50 }),
    enabled: canRead,
  });

  if (!canRead) return null;

  const mine = (query.data?.items ?? []).filter((i) => i.affectedOrganizationIds.includes(orgId));
  const myAbuse = (abuse.data?.items ?? []).filter((r) => r.organizationId === orgId).slice(0, 5);

  return (
    <Section
      title="直近インシデント"
      actions={
        <Link to="/incidents" search={{}} className="text-xs text-primary hover:underline">
          障害一覧へ
        </Link>
      }
    >
      {query.isError ? (
        <QueryError error={query.error} onRetry={() => void query.refetch()} />
      ) : query.isLoading ? (
        <p className="text-sm text-muted-foreground">読み込み中…</p>
      ) : mine.length === 0 ? (
        <p className="rounded-lg border border-border bg-card px-4 py-3 text-sm text-muted-foreground">
          直近 50 件の障害に、この契約組織が影響先として記録されたものはありません。
        </p>
      ) : (
        <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
          {mine.slice(0, 5).map((i) => (
            <li key={i.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-sm">
              <Link
                to="/incidents"
                search={{ incidentId: i.id }}
                className="font-mono text-xs text-primary hover:underline"
              >
                {i.reference}
              </Link>
              <span className="min-w-0 flex-1 truncate font-medium">{i.title}</span>
              <StatusBadge status={i.severity} size="sm" />
              <StatusBadge status={i.status} size="sm" />
              <span className="text-xs text-muted-foreground">
                {formatDateTime(i.startedAt)}
                {i.resolvedAt ? ` — 復旧 ${formatRelative(i.resolvedAt)}` : ' — 継続中'}
              </span>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-4">
        <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">迷惑行為の通報</p>
        {abuse.isError ? (
          <QueryError error={abuse.error} onRetry={() => void abuse.refetch()} />
        ) : abuse.isLoading ? (
          <p className="text-sm text-muted-foreground">読み込み中…</p>
        ) : myAbuse.length === 0 ? (
          <p className="rounded-lg border border-border bg-card px-4 py-3 text-sm text-muted-foreground">
            直近 50 件の通報に、この契約組織のものはありません。
          </p>
        ) : (
          <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
            {myAbuse.map((r) => (
              <li key={r.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-sm">
                <StatusBadge status={r.severity} size="sm" />
                <span className="shrink-0 text-xs text-muted-foreground">{labelOf(r.kind)}</span>
                <span className="min-w-0 flex-1 truncate">{r.summary || '（内容の記録なし）'}</span>
                <StatusBadge status={r.state} size="sm" />
                <span className="text-xs text-muted-foreground">{formatRelative(r.detectedAt)}</span>
                <Link to="/security" search={{ tab: 'abuse' }} className="text-xs text-primary hover:underline">
                  対処する
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Section>
  );
}

/* --------------------------------------------------------- 危険な操作 */

/**
 * ノード間移設（A150 / A319）と削除予約（R78）。
 *
 * どちらも API は R0 から在るのに画面が無く、DB を直接触るしかなかった。
 * 取り返しのつかない操作なので、通常の編集とは別のセクションに隔離し、
 * 確認テキストの入力を要求する。
 */
function DangerZone({ org }: { org: OrganizationDetail }): React.ReactElement {
  const canSuspend = usePermission(OPS_PERMISSION.orgSuspend);
  const [migrateOpen, setMigrateOpen] = React.useState(false);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [terminateOpen, setTerminateOpen] = React.useState(false);
  const [revokeOpen, setRevokeOpen] = React.useState(false);

  /*
   * **撤回の入口は `terminate_after` が立っているかで決める。**
   *
   * 「契約プラン」画面の解約取り消しは `cancelAtPeriodEnd` が真のときしか
   * 出ない。ところが即時解約（`status:'canceled'`）はサーバー側で
   * `cancelAtPeriodEnd = false` を必ず書くので、**即時解約した契約からは
   * 撤回メニューが永久に消える**。未払い経路（`POST /orgs/:id/terminate`）も
   * 同じで、どちらも DB を直接更新するしか戻す手が無かった。
   */
  const terminating = org.status === 'closing' || org.terminateAfter !== null;

  return (
    <Section
      title="危険な操作"
      description="実行すると契約者のメール配送に直接影響します。理由は必ず監査ログに残ります。"
      className="rounded-lg border border-destructive/40 p-4"
    >
      {terminating ? (
        <Alert variant="destructive" className="mb-4">
          <AlertTitle>この組織は解約手続き中です</AlertTitle>
          <AlertDescription>
            送信は停止済みです（受信は猶予期間中も止めません）。
            {org.terminateAfter ? `メールデータの削除予定は ${formatDateTime(org.terminateAfter)} です。` : null}
            誤操作や請求の行き違いなら、下の「解約を撤回する」で削除予定を外せます （猶予が過ぎると Maildir
            は物理削除され、戻せません）。
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={() => setMigrateOpen(true)}>
          <MoveRight aria-hidden />
          別のノードへ移設
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="text-destructive"
          onClick={() => setDeleteOpen(true)}
          disabled={terminating}
        >
          <Trash2 aria-hidden />
          契約終了とデータ削除を予約
        </Button>
        {canSuspend ? (
          <>
            <Button
              variant="outline"
              size="sm"
              className="text-destructive"
              onClick={() => setTerminateOpen(true)}
              disabled={terminating}
            >
              <Ban aria-hidden />
              回収を諦めて契約を畳む
            </Button>
            <Button variant="outline" size="sm" onClick={() => setRevokeOpen(true)} disabled={!terminating}>
              <Undo2 aria-hidden />
              解約を撤回する
            </Button>
          </>
        ) : null}
      </div>

      <MigrateOrganizationDialog org={org} open={migrateOpen} onOpenChange={setMigrateOpen} />
      <DeleteOrganizationDialog org={org} open={deleteOpen} onOpenChange={setDeleteOpen} />
      <TerminateOrganizationDialog org={org} open={terminateOpen} onOpenChange={setTerminateOpen} />
      <RevokeTerminationDialog org={org} open={revokeOpen} onOpenChange={setRevokeOpen} />
    </Section>
  );
}

/** `terminate` / `terminate/revoke` の後始末（詳細と一覧を引き直す）。 */
function useTerminationMutation(orgId: string, path: string, onDone: () => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) => api.post<{ accepted: boolean }>(`/orgs/${orgId}${path}`, { reason }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.org(orgId) });
      void qc.invalidateQueries({ queryKey: ['ops', 'orgs'] });
      void qc.invalidateQueries({ queryKey: ['ops', 'subscriptions'] });
      onDone();
    },
  });
}

/**
 * 未払いの終端（`POST /ops/orgs/:orgId/terminate`）。
 *
 * 自動の書き落とし（支払期限から 90 日）だけだと、回収を諦めると決めた契約を
 * その場で畳めず、`suspended` のまま月次締めが回り続けて回収見込みの無い
 * 売掛とストレージが積み上がる。
 *
 * **この API を叩いた瞬間にデータは消えない**（猶予の後に sweep が消す）。
 * `requireFreshAuth` 対象なので、必要なら共通の再認証ダイアログが挟まる。
 */
function TerminateOrganizationDialog({
  org,
  open,
  onOpenChange,
}: {
  org: OrganizationDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const [reason, setReason] = React.useState('');

  React.useEffect(() => {
    if (open) setReason('');
  }, [open]);

  const terminate = useTerminationMutation(org.id, '/terminate', () => onOpenChange(false));

  return (
    <ConfirmTextDialog
      open={open}
      onOpenChange={onOpenChange}
      title="回収を諦めて契約を畳む"
      description="未払いの終端です。送信を止め、猶予の経過後にメールデータを削除します。理由は監査ログに残ります。"
      confirmText={org.slug}
      confirmLabel="契約を畳む"
      pending={terminate.isPending}
      error={terminate.error}
      onConfirm={() => {
        if (reason.trim().length >= 5) terminate.mutate(reason.trim());
      }}
    >
      <Alert variant="warning">
        <AlertDescription>
          押した瞬間にデータは消えません。削除予定日が立つだけで、その前なら「解約を撤回する」で戻せます。
        </AlertDescription>
      </Alert>
      <Field
        label="理由"
        required
        hint="5 文字以上。後から経緯を再構成できる内容にしてください。"
        // 短いまま押しても何も起きない実装なので、押す前に理由を見せる。
        error={reason.length > 0 && reason.trim().length < 5 ? '5 文字以上で入力してください。' : undefined}
      >
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="3 か月連続で入金が無く、督促にも応答が無いため回収を断念"
        />
      </Field>
    </ConfirmTextDialog>
  );
}

/**
 * 解約の撤回（`POST /ops/orgs/:orgId/terminate/revoke`）。
 *
 * **撤回の経路が無いと、解約は片道切符になる。** メールボックスの復帰・
 * 契約の再開・ノードへの再配布まで worker 側の 1 か所（`contract.revoke-termination`）
 * が戻す。確認テキストは要求しない（取り返しの効く方向の操作なので、
 * 事故のときに素早く押せることのほうが大事）。
 */
function RevokeTerminationDialog({
  org,
  open,
  onOpenChange,
}: {
  org: OrganizationDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const [reason, setReason] = React.useState('');

  React.useEffect(() => {
    if (open) setReason('');
  }, [open]);

  const revoke = useTerminationMutation(org.id, '/terminate/revoke', () => onOpenChange(false));

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="解約を撤回する"
      description="削除予定を外し、メールボックスと契約を元に戻します。既に削除済みの組織は戻せません。"
      submitLabel="撤回する"
      pending={revoke.isPending}
      error={revoke.error}
      disabled={reason.trim().length < 5}
      onSubmit={() => revoke.mutate(reason.trim())}
    >
      <Alert>
        <AlertDescription>
          {org.terminateAfter
            ? `現在の削除予定は ${formatDateTime(org.terminateAfter)} です。`
            : '削除予定日が記録されていません。'}
          撤回はノードへの再配布まで含めて worker が行うので、反映まで少し時間がかかります。
        </AlertDescription>
      </Alert>
      <Field label="理由" required hint="5 文字以上。誤操作なのか、契約者との合意で戻すのかを書きます。">
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="入金の行き違いによる誤った即時解約のため撤回"
        />
      </Field>
    </FormDialog>
  );
}

/**
 * 組織のノード間移設（`POST /ops/orgs/:orgId/migrate`）。
 *
 * 202 でプロビジョニングジョブが返るので、進行はジョブ詳細へ誘導する
 * （この画面には進捗が出ない）。
 */
function MigrateOrganizationDialog({
  org,
  open,
  onOpenChange,
}: {
  org: OrganizationDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const navigate = routeApi.useNavigate();
  const [targetNodeId, setTargetNodeId] = React.useState('');

  const nodes = useQuery({
    queryKey: qk.nodes({ scope: 'migrate-target' }),
    queryFn: () => api.get<{ items: NodeSummary[] }>('/nodes', { limit: 200 }),
    enabled: open,
  });

  React.useEffect(() => {
    if (open) setTargetNodeId('');
  }, [open]);

  const migrate = useMutation({
    mutationFn: () => api.post<{ jobId: string }>(`/orgs/${org.id}/migrate`, { targetNodeId }),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: qk.org(org.id) });
      onOpenChange(false);
      void navigate({ to: '/jobs/$jobId', params: { jobId: res.jobId } });
    },
  });

  // 現在の主収容ノードは移設先にならない。
  const candidates = (nodes.data?.items ?? []).filter((n) => n.id !== org.primaryNodeId);

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="別のノードへ移設する"
      description={`${org.displayName} のドメインとメールボックスを別ノードへ移します。`}
      submitLabel="移設を開始"
      pending={migrate.isPending}
      error={migrate.error}
      disabled={targetNodeId.length === 0}
      onSubmit={() => migrate.mutate()}
    >
      <Alert variant="warning">
        <AlertDescription>
          移設中は配送が一時的に遅延します。Maildir の転送量が大きいほど時間がかかるので、
          夜間に実行してください。進行はプロビジョニングジョブで追跡します。
        </AlertDescription>
      </Alert>

      <Field label="移設先ノード" required hint={`現在の主収容: ${org.primaryNodeHostname ?? '未設定'}`}>
        <NativeSelect
          value={targetNodeId}
          onChange={setTargetNodeId}
          options={[
            { value: '', label: '選択してください' },
            ...candidates.map((n) => ({ value: n.id, label: `${n.hostname}（${n.region}）` })),
          ]}
        />
      </Field>
    </FormDialog>
  );
}

/**
 * 契約終了とデータ削除の予約（`DELETE /ops/orgs/:orgId`）。
 *
 * サーバーは `.strict()` で `reason`（5 文字以上）と `confirmSlug` が必須。
 * 有効な契約（trialing / active / past_due）が残っていると 400 で止まるので、
 * その旨を先に画面へ出す。**受信は猶予期間中も止めない**（取引先という
 * 第三者にいきなり不達を出さないため）。
 */
function DeleteOrganizationDialog({
  org,
  open,
  onOpenChange,
}: {
  org: OrganizationDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const [reason, setReason] = React.useState('');
  const [graceDays, setGraceDays] = React.useState('30');
  const [purgeAfterDays, setPurgeAfterDays] = React.useState('90');

  React.useEffect(() => {
    if (!open) return;
    setReason('');
    setGraceDays('30');
    setPurgeAfterDays('90');
  }, [open]);

  const remove = useMutation({
    mutationFn: () =>
      api.del(`/orgs/${org.id}`, {
        body: {
          reason: reason.trim(),
          confirmSlug: org.slug,
          graceDays: Number(graceDays || '0'),
          purgeAfterDays: Number(purgeAfterDays || '0'),
        },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.org(org.id) });
      void qc.invalidateQueries({ queryKey: ['ops', 'orgs'] });
      onOpenChange(false);
    },
  });

  const liveSubscription = ['trialing', 'active', 'past_due'].includes(org.subscriptionStatus);

  return (
    <ConfirmTextDialog
      open={open}
      onOpenChange={onOpenChange}
      title="契約終了とデータ削除を予約する"
      description="送信を即座に停止し、猶予期間の経過後にメールデータと個人データを消去します。"
      confirmText={org.slug}
      confirmLabel="削除を予約する"
      pending={remove.isPending}
      error={remove.error}
      onConfirm={() => {
        if (!liveSubscription && reason.trim().length >= 5) remove.mutate();
      }}
    >
      {liveSubscription ? (
        <Alert variant="destructive">
          <AlertDescription>
            有効な契約（{labelOf(org.subscriptionStatus)}）が残っています。 先に「契約プラン」画面から解約してください。
          </AlertDescription>
        </Alert>
      ) : (
        <Alert variant="warning">
          <AlertDescription>
            送信だけを止めます。<strong>受信は猶予期間中も止めません</strong>
            （取引先という第三者にいきなり不達を出さないため）。予告は契約者へ必ず送られます。
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-3">
        <Field label="理由" required hint="5 文字以上。契約者への通知本文と監査ログに載ります。">
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="契約者からの解約申し出（チケット #1234）に伴う契約終了"
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="メールデータの削除まで（日）" hint="既定 30 日。この間はエクスポートできます。">
            <Input
              value={graceDays}
              onChange={(e) => setGraceDays(e.target.value.replace(/[^0-9]/g, ''))}
              inputMode="numeric"
            />
          </Field>
          <Field label="個人データの消去まで（日）" hint="既定 90 日。宛先名簿などが対象です。">
            <Input
              value={purgeAfterDays}
              onChange={(e) => setPurgeAfterDays(e.target.value.replace(/[^0-9]/g, ''))}
              inputMode="numeric"
            />
          </Field>
        </div>
      </div>
    </ConfirmTextDialog>
  );
}

/**
 * 契約組織のマスタ修正（`PATCH /ops/orgs/:orgId`）。
 *
 * **なぜ運営側に要るか。** 契約者がログインできない状態（未納停止中・管理者が
 * 退職して誰も入れない・請求先メールが到達不能）だと、請求書の宛名と請求先を
 * 直す手段がどこにも無かった。請求の前提が壊れたまま督促だけが飛ぶ。
 *
 * サーバーは `.strict()` で **`reason`（5 文字以上）が必須**。`slug` は URL と
 * ノード上のパスに波及するのでここでは変更できない。
 */
function EditOrganizationDialog({
  org,
  open,
  onOpenChange,
}: {
  org: OrganizationDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const [form, setForm] = React.useState({
    displayName: org.displayName,
    billingEmail: org.billingEmail,
    contactName: org.contactName ?? '',
    contactPhone: org.contactPhone ?? '',
    notes: org.notes ?? '',
  });
  const [reason, setReason] = React.useState('');

  React.useEffect(() => {
    if (!open) return;
    setForm({
      displayName: org.displayName,
      billingEmail: org.billingEmail,
      contactName: org.contactName ?? '',
      contactPhone: org.contactPhone ?? '',
      notes: org.notes ?? '',
    });
    setReason('');
  }, [open, org]);

  const mut = useMutation({
    mutationFn: () => {
      // 変わった項目だけを送る。サーバーは「変更する項目がありません」を 400 で返す。
      const body: Record<string, unknown> = { reason: reason.trim() };
      if (form.displayName.trim() !== org.displayName) body.displayName = form.displayName.trim();
      if (form.billingEmail.trim() !== org.billingEmail) body.billingEmail = form.billingEmail.trim();
      if (form.contactName.trim() !== (org.contactName ?? '')) {
        body.contactName = form.contactName.trim() === '' ? null : form.contactName.trim();
      }
      if (form.contactPhone.trim() !== (org.contactPhone ?? '')) {
        body.contactPhone = form.contactPhone.trim() === '' ? null : form.contactPhone.trim();
      }
      if (form.notes !== (org.notes ?? '')) body.notes = form.notes === '' ? null : form.notes;
      return api.patch(`/orgs/${org.id}`, body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.org(org.id) });
      void qc.invalidateQueries({ queryKey: qk.orgs() });
      onOpenChange(false);
    },
  });

  const changed =
    form.displayName.trim() !== org.displayName ||
    form.billingEmail.trim() !== org.billingEmail ||
    form.contactName.trim() !== (org.contactName ?? '') ||
    form.contactPhone.trim() !== (org.contactPhone ?? '') ||
    form.notes !== (org.notes ?? '');

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`${org.displayName} のマスタを修正`}
      description="契約者のマスタ情報を運営が書き換える操作です。変更前後の値と理由が監査ログに残ります。スラッグは変更できません。"
      submitLabel="修正を保存する"
      pending={mut.isPending}
      error={mut.isError ? mut.error : undefined}
      disabled={reason.trim().length < 5 || !changed}
      onSubmit={() => mut.mutate()}
      wide
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="組織名" required hint="請求書の宛名になります。">
          <Input
            value={form.displayName}
            onChange={(e) => setForm({ ...form, displayName: e.target.value })}
            autoComplete="off"
          />
        </Field>
        <Field label="請求先メールアドレス" required hint="請求書と重要通知の宛先。">
          <Input
            type="email"
            value={form.billingEmail}
            onChange={(e) => setForm({ ...form, billingEmail: e.target.value })}
            autoComplete="off"
          />
        </Field>
        <Field label="担当者名">
          <Input
            value={form.contactName}
            onChange={(e) => setForm({ ...form, contactName: e.target.value })}
            autoComplete="off"
          />
        </Field>
        <Field label="電話番号">
          <Input
            value={form.contactPhone}
            onChange={(e) => setForm({ ...form, contactPhone: e.target.value })}
            autoComplete="off"
          />
        </Field>
      </div>

      <Field label="運営メモ" hint="契約者には見えません。">
        <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} />
      </Field>

      <Field label="理由" required hint="5 文字以上。誰の依頼で何を直したかを書いてください。">
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
      </Field>
    </FormDialog>
  );
}

/* ----------------------------------------------------------------- domains */

function DomainsTab({ orgId }: { orgId: string }): React.ReactElement {
  const list = useCursorList<DomainSummary>({
    path: `/orgs/${orgId}/domains`,
    keyFor: (params) => qk.orgDomains(orgId, params),
    filters: {},
  });

  const columns: Array<DataTableColumn<DomainSummary>> = [
    {
      id: 'name',
      header: 'ドメイン',
      cell: (d) => (
        <Link
          to="/domains/$domainId"
          params={{ domainId: d.id }}
          search={{ tab: 'overview' }}
          className="font-medium hover:text-primary"
        >
          {d.name}
        </Link>
      ),
    },
    { id: 'status', header: '状態', cell: (d) => <StatusBadge status={d.status} size="sm" /> },
    { id: 'node', header: '収容ノード', hideBelow: 'md', cell: (d) => d.nodeHostname ?? '—' },
    { id: 'mailboxes', header: 'メールボックス', align: 'right', cell: (d) => formatCount(d.mailboxCount) },
    { id: 'created', header: '追加日', hideBelow: 'lg', cell: (d) => formatDateTime(d.createdAt) },
  ];

  return <ListPanel list={list} columns={columns} rowKey={(d) => d.id} emptyTitle="ドメインがありません" />;
}

/* -------------------------------------------------------------- mailboxes */

function MailboxesTab({ orgId }: { orgId: string }): React.ReactElement {
  const list = useCursorList<MailboxSummary>({
    path: `/orgs/${orgId}/mailboxes`,
    keyFor: (params) => qk.orgMailboxes(orgId, params),
    filters: {},
  });

  const columns: Array<DataTableColumn<MailboxSummary>> = [
    { id: 'address', header: 'メールアドレス', cell: (m) => <span className="font-medium">{m.address}</span> },
    { id: 'status', header: '状態', cell: (m) => <StatusBadge status={m.status} size="sm" /> },
    {
      id: 'quota',
      header: '容量',
      width: 'w-44',
      cell: (m) => <UsageBar used={m.usedBytes} total={m.quotaBytes} formatValue={(v) => formatBytes(v)} />,
    },
    { id: 'lastLogin', header: '最終ログイン', hideBelow: 'md', cell: (m) => formatRelative(m.lastLoginAt) },
    { id: 'sent', header: '24h 送信', align: 'right', hideBelow: 'lg', cell: (m) => formatCount(m.sent24h) },
  ];

  /*
   * **設定情報シート（W-ONBOARD §3-4）。**
   *
   * サポートで「設定が分からない」と言われたときに、運営がその場で
   * 契約者へ渡すのと同じ紙を出せるようにする。ここでは**いま表示中の
   * ページぶん**をまとめて印刷する（次ページ以降は含めない ——
   * 見えていない件数を「全部」と称して刷らない）。
   * **パスワードは印字されない**ので `orgRead` だけで足りる。
   */
  const visibleIds = list.rows.map((m) => m.id);
  const printHref = `/print/mail-setup?ids=${encodeURIComponent(visibleIds.join(','))}`;

  return (
    <div className="space-y-3">
      {visibleIds.length > 0 ? (
        <div className="flex justify-end">
          <Button asChild variant="outline" size="sm">
            {/* 別タブ。同じタブだとタブ選択とページ位置が失われる。 */}
            <a href={printHref} target="_blank" rel="noopener noreferrer">
              <Printer className="size-4" aria-hidden />
              このページの設定情報を印刷（{visibleIds.length} 件）
            </a>
          </Button>
        </div>
      ) : null}
      <ListPanel list={list} columns={columns} rowKey={(m) => m.id} emptyTitle="メールボックスがありません" />
    </div>
  );
}

/* ------------------------------------------------------------------ usage */

function UsageTab({ orgId }: { orgId: string }): React.ReactElement {
  const query = useQuery({
    queryKey: qk.orgUsage(orgId, '12m'),
    queryFn: () =>
      api.get<{
        points: Array<{ at: string; mailboxes: number; storageBytes: number; sent: number }>;
      }>(`/orgs/${orgId}/usage`, { granularity: 'month' }),
  });

  const points = query.data?.points ?? [];
  const mailboxSeries: Series[] = [
    {
      key: 'mailboxes',
      label: 'メールボックス数',
      className: 'stroke-primary',
      points: points.map((p) => ({ at: p.at, value: p.mailboxes })),
    },
  ];
  const storageSeries: Series[] = [
    {
      key: 'storage',
      label: 'ストレージ',
      className: 'stroke-info',
      points: points.map((p) => ({ at: p.at, value: p.storageBytes })),
    },
  ];
  const sentSeries: Series[] = [
    {
      key: 'sent',
      label: '送信通数',
      className: 'stroke-warning',
      points: points.map((p) => ({ at: p.at, value: p.sent })),
    },
  ];

  return (
    <div className="space-y-6">
      {query.isError ? <QueryError error={query.error} onRetry={() => void query.refetch()} /> : null}
      <Section title="メールボックス数の推移">
        <LineChart series={mailboxSeries} />
      </Section>
      <Section title="ストレージの推移">
        <LineChart series={storageSeries} formatValue={(n) => formatBytes(n)} />
      </Section>
      <Section title="送信通数の推移">
        <LineChart series={sentSeries} />
      </Section>
    </div>
  );
}

/* --------------------------------------------------------------- invoices */

function InvoicesTab({ orgId }: { orgId: string }): React.ReactElement {
  const canBillingWrite = usePermission(OPS_PERMISSION.billingWrite);
  const [paymentMethodOpen, setPaymentMethodOpen] = React.useState(false);
  const query = useQuery({
    queryKey: qk.orgInvoices(orgId),
    queryFn: () => api.get<{ items: InvoiceSummary[] }>(`/orgs/${orgId}/invoices`),
  });

  const columns: Array<DataTableColumn<InvoiceSummary>> = [
    {
      id: 'number',
      header: '請求書番号',
      cell: (i) => (
        <Link to="/invoices/$invoiceId" params={{ invoiceId: i.id }} className="font-medium hover:text-primary">
          {i.number}
        </Link>
      ),
    },
    {
      id: 'period',
      header: '対象期間',
      cell: (i) => `${formatDateTime(i.periodStart)} 〜 ${formatDateTime(i.periodEnd)}`,
    },
    { id: 'total', header: '金額', align: 'right', cell: (i) => formatJpy(i.totalJpy) },
    { id: 'status', header: '状態', cell: (i) => <StatusBadge status={i.status} size="sm" /> },
    { id: 'due', header: '支払期限', hideBelow: 'md', cell: (i) => formatDateTime(i.dueAt) },
  ];

  return (
    <>
      {/*
        **運営が代わりに請求書払いを登録できる入口。**
        `billing_customers` に行が無いと `handleTrialClose` の
        `hasPaymentMethod` が false になり、トライアル終了で `past_due` →
        送信停止へ落ちる。電話・メールで申し込んだ法人（コントロールパネルを
        触らない契約者）には、この画面から入れるしかない。
        `POST /ops/orgs/:orgId/payment-method` は実装済みだったのに、
        **呼ぶ画面がリポジトリ全体で 0 件**だった。
      */}
      {canBillingWrite ? (
        <div className="mb-3 flex justify-end">
          <Button variant="outline" size="sm" onClick={() => setPaymentMethodOpen(true)}>
            <CreditCard aria-hidden />
            請求書払いを承認する
          </Button>
        </div>
      ) : null}

      {query.isError ? <QueryError error={query.error} onRetry={() => void query.refetch()} /> : null}
      <DataTable
        columns={columns}
        rows={query.data?.items ?? []}
        rowKey={(i) => i.id}
        loading={query.isLoading}
        emptyTitle="請求書はまだありません"
      />

      <PaymentMethodDialog orgId={orgId} open={paymentMethodOpen} onOpenChange={setPaymentMethodOpen} />
    </>
  );
}

/**
 * 運営代行での請求書払い（銀行振込）の登録＝**与信の承認**。
 *
 * カード（`kind:'card'`）はここでは受けない。カード情報に触れる経路は
 * Stripe の Checkout だけにしてある。
 *
 * 支払サイトと与信枠を同じ操作で決めるのは、分けると「請求書払いにはしたが
 * 与信は未審査」という行が残り、自動付与（`credit_reviewed_by='system'`）と
 * 区別が付かなくなるため。**省略した項目は据え置き**（既存契約の期日を
 * 無関係な更新のたびに動かさない）。
 */
const PAYMENT_TERMS_OPTIONS = [
  { value: '', label: '変更しない（据え置き）' },
  { value: 'eom_next_eom', label: labelOf('eom_next_eom') },
  { value: 'eom_next_25', label: labelOf('eom_next_25') },
  { value: 'eom_next_15', label: labelOf('eom_next_15') },
  { value: 'net30', label: labelOf('net30') },
  { value: 'net15', label: labelOf('net15') },
  { value: 'net60', label: labelOf('net60') },
  { value: 'prepaid', label: labelOf('prepaid') },
];

interface CreditState {
  paymentTerms: string;
  limit: number;
  outstanding: number;
  available: number | null;
  exceeded: boolean;
}

function PaymentMethodDialog({
  orgId,
  open,
  onOpenChange,
}: {
  orgId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const [note, setNote] = React.useState('');
  const [paymentTerms, setPaymentTerms] = React.useState('');
  const [creditLimit, setCreditLimit] = React.useState('');
  const [credit, setCredit] = React.useState<CreditState | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setNote('');
    setPaymentTerms('');
    setCreditLimit('');
    setCredit(null);
  }, [open]);

  const register = useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; created: boolean; credit: CreditState }>(`/orgs/${orgId}/payment-method`, {
        kind: 'invoice',
        ...(note.trim().length > 0 ? { note: note.trim() } : {}),
        // 空欄は送らない（サーバー側で「据え置き」になる）。
        ...(paymentTerms.length > 0 ? { paymentTerms } : {}),
        ...(creditLimit.trim().length > 0 ? { creditLimit: Number(creditLimit) } : {}),
      }),
    onSuccess: (res) => {
      setCredit(res.credit);
      void qc.invalidateQueries({ queryKey: qk.org(orgId) });
      void qc.invalidateQueries({ queryKey: ['ops', 'subscriptions'] });
    },
  });

  const limitInvalid = creditLimit.trim().length > 0 && !/^\d{1,10}$/.test(creditLimit.trim());

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="請求書払いを承認する"
      description="銀行振込（請求書払い）の決済手段を運営が代わりに登録します。カードはここでは登録できません。"
      submitLabel={credit ? '閉じる' : '登録する'}
      pending={register.isPending}
      error={register.error}
      disabled={limitInvalid}
      onSubmit={() => {
        if (credit) onOpenChange(false);
        else register.mutate();
      }}
      wide
    >
      {credit ? (
        <Alert variant={credit.exceeded ? 'warning' : 'success'}>
          <AlertTitle>登録しました</AlertTitle>
          <AlertDescription>
            支払サイト {labelOf(credit.paymentTerms)} / 与信枠 {credit.limit === 0 ? '無制限' : formatJpy(credit.limit)}{' '}
            / 未回収 {formatJpy(credit.outstanding)}
            {credit.exceeded ? '（与信枠を超過しています）' : ''}
          </AlertDescription>
        </Alert>
      ) : (
        <>
          <Alert>
            <AlertDescription>
              決済手段が 1 つも無い契約は、トライアル終了時に <strong>past_due</strong> へ落ちて送信が止まります。
              電話・メールで申し込んだ法人はここから登録してください。
            </AlertDescription>
          </Alert>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="支払サイト" hint="省略すると現在の設定を据え置きます。">
              <NativeSelect value={paymentTerms} onChange={setPaymentTerms} options={PAYMENT_TERMS_OPTIONS} />
            </Field>
            <Field
              label="与信枠（円・税込）"
              hint="0 は無制限。省略すると据え置きます。"
              error={limitInvalid ? '半角数字で入力してください。' : undefined}
            >
              <Input
                value={creditLimit}
                onChange={(e) => setCreditLimit(e.target.value)}
                inputMode="numeric"
                placeholder="500000"
              />
            </Field>
          </div>

          <Field label="与信の根拠" hint="審査結果・稟議番号など。監査ログに残ります（500 文字まで）。">
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={500} />
          </Field>
        </>
      )}
    </FormDialog>
  );
}

/* --------------------------------------------------------------- activity */

function ActivityTab({ orgId }: { orgId: string }): React.ReactElement {
  const query = useQuery({
    queryKey: qk.orgAudit(orgId),
    queryFn: () => api.get<{ items: AuditEntry[] }>('/audit', { organizationId: orgId, limit: 100 }),
  });

  const columns: Array<DataTableColumn<AuditEntry>> = [
    { id: 'at', header: '日時', width: 'w-44', cell: (e) => formatDateTime(e.occurredAt) },
    { id: 'actor', header: 'アクター', cell: (e) => `${labelOf(e.actorType)} / ${e.actorLabel}` },
    { id: 'action', header: '操作', cell: (e) => <code className="font-mono text-xs">{e.action}</code> },
    { id: 'target', header: '対象', hideBelow: 'md', cell: (e) => e.targetId ?? '—' },
    {
      id: 'result',
      header: '結果',
      cell: (e) => <StatusBadge status={e.success ? 'succeeded' : 'failed'} size="sm" />,
    },
  ];

  return (
    <>
      {query.isError ? <QueryError error={query.error} onRetry={() => void query.refetch()} /> : null}
      <DataTable
        columns={columns}
        rows={query.data?.items ?? []}
        rowKey={(e) => e.id}
        loading={query.isLoading}
        emptyTitle="この組織に関する記録はありません"
      />
    </>
  );
}

/* -------------------------------------------------------------- 共通パネル */

function ListPanel<T>({
  list,
  columns,
  rowKey,
  emptyTitle,
}: {
  list: ReturnType<typeof useCursorList<T>>;
  columns: Array<DataTableColumn<T>>;
  rowKey: (row: T) => string;
  emptyTitle: string;
}): React.ReactElement {
  return (
    <>
      {list.query.isError ? (
        <QueryError error={list.query.error} onRetry={() => void list.query.refetch()} className="mb-4" />
      ) : null}
      <DataTable
        columns={columns}
        rows={list.rows}
        rowKey={rowKey}
        loading={list.query.isLoading}
        emptyTitle={emptyTitle}
        footer={
          <CursorPagination
            nextCursor={list.nextCursor}
            canGoPrev={list.canGoPrev}
            onNext={list.goNext}
            onPrev={list.goPrev}
            limit={list.limit}
            onLimitChange={list.setLimit}
            currentCount={list.rows.length}
            loading={list.query.isFetching}
          />
        }
      />
    </>
  );
}

/* --------------------------------------------------------- 担当者の招待 */

/**
 * 契約組織に担当者を招待する（新規・再送を兼ねる）。
 *
 * ## なぜこの画面が要るのか
 *
 * これが無いと、**担当者不在の組織は永久に誰もログインできない**。
 * 契約者を作る口は 3 本しか無く、すべて塞がっていた:
 *
 *   - `POST /portal/org/members` … 契約者のログインが要る（その循環で困っている）
 *   - `POST /public/signup` … `LEGAL_*` 未設定なら 503
 *   - `POST /ops/orgs` の `ownerEmail` … **組織の作成時にしか通らない**
 *
 * 代理ログインでも塞げない（`org.member.write` は代理では常に 403。運営者が
 * 顧客組織に常設アカウントを作るのを塞ぐ意図的な禁止なので、緩めない）。
 *
 * ## 画面で明示していること
 *
 * - **パスワードは運営が決めない。** 招待リンクから本人が設定する。
 * - **理由が必須。** 「なぜ他社の組織にログインできる人を増やしたか」は
 *   監査に残さなければならない。
 * - **メールが送れなかったら閉じない。** 送信結果は `inviteSent` で返る。
 *   これを見ないと、担当者は招待リンクを一生受け取らない（リンクは Redis に
 *   sha256 しか残らないので復元もできない）。
 */
function InviteOwnerDialog({
  orgId,
  open,
  onOpenChange,
}: {
  orgId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const [email, setEmail] = React.useState('');
  const [displayName, setDisplayName] = React.useState('');
  const [reason, setReason] = React.useState('');

  React.useEffect(() => {
    if (!open) return;
    setEmail('');
    setDisplayName('');
    setReason('');
  }, [open]);

  const submit = useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; membershipId: string; inviteSent: boolean; alreadyActive: boolean }>(
        `/orgs/${orgId}/owner-invite`,
        { email: email.trim(), displayName: displayName.trim(), reason: reason.trim() },
      ),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: qk.org(orgId) });
      void qc.invalidateQueries({ queryKey: qk.orgs() });
      // 送れなかったときは閉じずに知らせる（この画面の要点）。
      if (!res.inviteSent && !res.alreadyActive) return;
      onOpenChange(false);
    },
  });

  const result = submit.isSuccess ? submit.data : null;
  const inviteFailed = result !== null && !result.inviteSent && !result.alreadyActive;
  const invalid = email.trim().length === 0 || displayName.trim().length === 0 || reason.trim().length < 5;

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="担当者を招待する"
      description="契約者パネルにログインできる人を追加します。既に居る場合は招待をやり直します。"
      submitLabel={inviteFailed ? '閉じる' : '招待する'}
      pending={submit.isPending}
      error={submit.error}
      disabled={inviteFailed ? false : invalid}
      onSubmit={() => (inviteFailed ? onOpenChange(false) : submit.mutate())}
    >
      {inviteFailed ? (
        <Alert variant="destructive">
          <AlertDescription>
            <strong>メンバーには追加できましたが、招待メールを送信できませんでした。</strong>
            このままでは本人はパスワードを設定できません。招待リンクは再表示できないため、
            <strong>メール送信の設定（NOTIFY_SMTP_*）を直したうえで、もう一度招待してください</strong>。
          </AlertDescription>
        </Alert>
      ) : (
        <Alert variant="warning">
          <AlertDescription>
            <strong>パスワードは運営者が設定できません。</strong>
            招待メールのリンクから本人が設定します。ここで追加した人は
            <strong>契約者パネルに完全な権限（オーナー）でログインできる</strong>ようになります。
          </AlertDescription>
        </Alert>
      )}

      <Field label="メールアドレス" required>
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="owner@example.jp"
          spellCheck={false}
        />
      </Field>

      <Field label="氏名" required>
        <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="山田 太郎" />
      </Field>

      <Field label="招待する理由" required hint="監査ログに残ります（5 文字以上）。">
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          placeholder="担当者不在のため、申込書の連絡先を招待"
        />
      </Field>
    </FormDialog>
  );
}
